import { useTranslation } from "react-i18next";

/**
 * Primary-action label key of the review: with nothing staged the honest offer
 * is "Alles korrekt"; once anything is staged or typed (even an invalid value
 * still open in an editor) it is "Speichern" (KC-57).
 *
 * There is deliberately no second confirmation step — the review is optional
 * and the check-in is already committed.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * t(reviewPrimaryLabelKey({ stagedCount: 0, hasInvalidInput: false })); // "Alles korrekt"
 * ```
 */
export function reviewPrimaryLabelKey({
  stagedCount,
  hasInvalidInput,
}: {
  readonly stagedCount: number;
  readonly hasInvalidInput: boolean;
}): string {
  return stagedCount > 0 || hasInvalidInput
    ? "self-service.check-in.patient-info.review.save"
    : "self-service.check-in.patient-info.review.all-correct";
}

/**
 * The balance line under the check sheet: what will happen to the staged
 * changes once the patient taps "Speichern" (future tense, KC-55). The actions
 * themselves are the step's footer actions.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <ReviewBalanceLine balance="2 Änderungen · Sofort gespeichert: 1" />
 * ```
 */
export function ReviewBalanceLine({ balance }: { balance?: string }) {
  const { t } = useTranslation();
  if (balance === undefined) return null;
  return (
    <p
      className="m-0 text-lg text-foreground"
      data-testid="patient-info-balance-line"
    >
      <span className="sr-only">
        {t("self-service.check-in.patient-info.review.balance-prefix")}
      </span>
      {balance}
    </p>
  );
}
